import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect } from 'react';
import { Card, Col, InputGroup, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import { FormProvider, RHFSelect } from 'components/common/form';
import LoadingButton from 'components/common/LoadingButton';
import AuthContext from 'context/authContext';
import { Can } from 'routes/permissions/Can';
import { userActions } from 'routes/permissions/permissionKeys';

const GET_WEBSITE_SETTINGS = gql`
  query GetWebsiteSettings($organizationId: uuid!) {
    website_settings(where: { organisation_id: { _eq: $organizationId } }) {
      default_package_id
      default_donation_type_id
      preselected_amount
    }
  }
`;

const SAVE_WEBSITE_SETTINGS = gql`
  mutation SaveWebsiteSettings(
    $organizationId: uuid!
    $defaultPackageId: uuid
    $defaultDonationTypeId: uuid
    $preselectedAmount: String
  ) {
    insert_website_settings_one(
      object: {
        organisation_id: $organizationId
        default_package_id: $defaultPackageId
        default_donation_type_id: $defaultDonationTypeId
        preselected_amount: $preselectedAmount
      }
      on_conflict: {
        constraint: website_settings_organisation_id_key
        update_columns: [
          default_package_id
          default_donation_type_id
          preselected_amount
        ]
      }
    ) {
      default_package_id
      default_donation_type_id
      preselected_amount
    }
  }
`;

const GET_DONATION_TYPES = gql`
  query GetDonationTypes($where: donation_type_bool_exp = {}) {
    donation_type(order_by: { created_at: desc }, where: $where) {
      id
      donation_type_name
      organisation_id
    }
  }
`;

const GET_PACKAGES = gql`
  query GetPackages($organizationId: uuid!) {
    packages(
      where: {
        organisation_id: { _eq: $organizationId }
        is_hidden: { _eq: false }
      }
    ) {
      id
      package_title
      simple_id
    }
  }
`;

const websiteSettingsSchema = Yup.object().shape({
  defaultPackageId: Yup.string().required('Default package is required'),
  defaultDonationTypeId: Yup.string().required(
    'Default donation type is required'
  ),
  preselectedAmount: Yup.string().required('Preselected amount is required')
});

const WebsiteSettings = ({ sectionRef, sideNavLink, subject }) => {
  const { organization } = useContext(AuthContext);
  const [getWebsiteSettings, { data: websiteSettingsData }] =
    useLazyQuery(GET_WEBSITE_SETTINGS);
  const [saveWebsiteSettings, { loading: isSaveWebsiteSettingsLoading }] =
    useMutation(SAVE_WEBSITE_SETTINGS, {
      onCompleted: () => {
        toast.success('Website settings have been updated');
      }
    });

  const [getDonationTypes, { data: donationTypesData }] =
    useLazyQuery(GET_DONATION_TYPES);
  const [getPackages, { data: packagesData }] = useLazyQuery(GET_PACKAGES);

  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      defaultPackageId: '',
      defaultDonationTypeId: '',
      preselectedAmount: ''
    },
    resolver: yupResolver(websiteSettingsSchema)
  });
  const { handleSubmit: RHFHandleSubmit, reset: resetForm } = methods;

  const onSave = data => {
    saveWebsiteSettings({
      variables: {
        organizationId: organization.id,
        defaultPackageId: data.defaultPackageId,
        defaultDonationTypeId: data.defaultDonationTypeId,
        preselectedAmount: data.preselectedAmount
      }
    });
  };

  useEffect(() => {
    if (organization && organization.id) {
      getWebsiteSettings({
        variables: {
          organizationId: organization.id
        }
      });
      getDonationTypes({
        variables: {
          where: { organisation_id: { _eq: organization.id } }
        }
      });
      getPackages({
        variables: {
          organizationId: organization.id
        }
      });
    }
  }, [organization, getWebsiteSettings, getDonationTypes, getPackages]);

  useEffect(() => {
    if (websiteSettingsData) {
      resetForm({
        defaultPackageId:
          websiteSettingsData.website_settings[0]?.default_package_id || '',
        defaultDonationTypeId:
          websiteSettingsData.website_settings[0]?.default_donation_type_id ||
          '',
        preselectedAmount:
          websiteSettingsData.website_settings[0]?.preselected_amount || ''
      });
    }
  }, [websiteSettingsData, resetForm]);

  const donationTypeOptions =
    donationTypesData?.donation_type?.map(({ id, donation_type_name }) => ({
      label: donation_type_name,
      value: id
    })) || [];

  const packageOptions =
    packagesData?.packages?.map(({ id, package_title, simple_id }) => ({
      label: `${simple_id} | ${package_title}`,
      value: id
    })) || [];

  const preselectedAmountOptions = [
    { label: 'Lowest', value: 'lowest' },
    { label: 'Average', value: 'average' },
    { label: 'Highest', value: 'highest' }
  ];

  return (
    <FormProvider methods={methods} onSubmit={RHFHandleSubmit(onSave)}>
      <Card
        ref={sectionRef}
        id={sideNavLink}
        className="border shadow-none mb-3"
      >
        <Card.Header>
          <Row>
            <Col>
              <Card.Title>Website Settings</Card.Title>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <InputGroup className="mb-3">
            <div className="flex-grow-1">
              <RHFSelect
                name="defaultPackageId"
                label="Default Package"
                options={packageOptions}
                placeholder="Select Default Package"
                required
              />
            </div>
          </InputGroup>
          <InputGroup className="mb-3">
            <div className="flex-grow-1">
              <RHFSelect
                name="defaultDonationTypeId"
                label="Default Donation Type"
                options={donationTypeOptions}
                placeholder="Select Default Donation Type"
                required
              />
            </div>
          </InputGroup>
          <InputGroup className="mb-3">
            <div className="flex-grow-1">
              <RHFSelect
                name="preselectedAmount"
                label="Preselected Amount"
                options={preselectedAmountOptions}
                placeholder="Select Preselected Amount"
                required
              />
            </div>
          </InputGroup>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-end">
          <Can I={userActions.update} a={subject}>
            <LoadingButton
              loading={isSaveWebsiteSettingsLoading}
              variant="light"
              type="submit"
              className="border bg-transparent shadow-none align-self-end"
              style={{ fontWeight: 600 }}
            >
              Save
            </LoadingButton>
          </Can>
        </Card.Footer>
      </Card>
    </FormProvider>
  );
};

WebsiteSettings.propTypes = {
  sectionRef: PropTypes.object,
  sideNavLink: PropTypes.string,
  subject: PropTypes.string
};

export default WebsiteSettings;
